import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/toaster";

interface QueuedCategory {
  id: string;
  sourceNeed: string;
  groundingMode: string;
  rootCategory: number | null;
  titleTerms: string[];
  evidence: string[];
  questions: Array<{ label: string; question: string; required: boolean }>;
  guardrails: string[];
  reviewedAt: string | null;
  createdAt: string;
  useCount: number;
}

interface UnmetRequest {
  need: string;
  count: number;
  /** Distinct people we can name — never includes the unattributable rows. */
  distinctAskers: number;
  /** Rows whose asker is unknown (the account-deletion cascade), counted on their own. */
  unattributed: number;
  lastAskedAt: string;
}

/** Mirrors `InferenceCostRollup` in functions/lib/cost-ledger.ts. */
interface InferenceCostRollup {
  eventCount: number;
  localEstimateUsd: number;
  unpricedEventCount: number;
  gatewayCostUsd: number;
  unreconciledEventCount: number;
  cacheHitCount: number;
  cacheKnownCount: number;
}

interface SessionLedgerEntry {
  sessionId: string;
  category: string;
  openedAt: string;
  quotedFeeCents: number;
  feeWaived: boolean;
  commissionCents: number | null;
  inference: InferenceCostRollup;
  keepaTokensConsumed: number;
  keepaCostEur: number;
  keepaCostUsd: number | null;
}

interface CategoryBuildLedgerEntry {
  categoryId: string;
  buildCount: number;
  inference: InferenceCostRollup;
  keepaTokensConsumed: number;
  keepaCostEur: number;
  keepaCostUsd: number | null;
}

interface CostLedger {
  sessions: SessionLedgerEntry[];
  builds: CategoryBuildLedgerEntry[];
  /** The two spans this rollup covers — see `CostLedger.window` server-side. */
  window: {
    sessionLimit: number;
    sessionCount: number;
    buildsAreAllTime: true;
  };
  totals: {
    sessionCount: number;
    sessionsWithUnknownCostCount: number;
    inference: InferenceCostRollup;
    buildInference: InferenceCostRollup;
    sessionKeepaTokens: number;
    buildKeepaTokens: number;
    sessionKeepaCostEur: number;
    sessionKeepaCostUsd: number | null;
    buildKeepaCostEur: number;
    buildKeepaCostUsd: number | null;
    quotedFeeCents: number;
    knownCommissionCents: number;
    unknownCommissionCount: number;
    sessionCacheHitRate: number | null;
    buildCacheHitRate: number | null;
  };
  eurUsdRate: number | null;
}

/** Small USD figures: cents are useless here, the numbers are fractions of one. */
function usd(value: number): string {
  return `$${value.toFixed(6)}`;
}

function cents(value: number): string {
  return `$${(value / 100).toFixed(2)}`;
}

/**
 * A cost total, with the count of things it could not price attached.
 *
 * Never renders the total alone. Every sum here is a floor, and a floor shown
 * without its unknown count reads as a complete number — which, for a margin
 * view, is the difference between "we are profitable" and "we have no idea".
 */
function floorWithUnknowns(total: number, unknownCount: number, unit: string): string {
  if (unknownCount === 0) return `${usd(total)} ${unit}`;
  return `${usd(total)} ${unit} + ${unknownCount} unknown`;
}

/**
 * Volume and attribution, side by side and never merged.
 *
 * `count` alone can't tell one person searching five times from five people
 * searching once, which is exactly the difference that decides what deserves
 * hand-authoring next. The unattributed rows get their own number rather than
 * being dropped (which would understate demand) or counted as one phantom
 * asker (which would overstate it) — and "0 known askers" is worded that way
 * so it reads as "nobody we can name", never as "nobody asked": the request
 * count standing next to it says people did.
 */
function askerSummary(request: UnmetRequest): string {
  const askers = `${request.distinctAskers} known ${request.distinctAskers === 1 ? "asker" : "askers"}`;
  const unknown = request.unattributed > 0 ? ` (${request.unattributed} unattributed)` : "";
  return `${request.count}× · ${askers}${unknown}`;
}

export function AdminPage() {
  const [categories, setCategories] = useState<QueuedCategory[] | null>(null);
  const [unmet, setUnmet] = useState<UnmetRequest[]>([]);
  const [ledger, setLedger] = useState<CostLedger | null>(null);
  const [denied, setDenied] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reconciling, setReconciling] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    const response = await fetch("/api/admin/queue", { credentials: "include" });
    // 404 is what a non-admin gets — the endpoint doesn't acknowledge itself.
    if (!response.ok) {
      setDenied(true);
      setCategories([]);
      return;
    }
    const data = (await response.json()) as {
      categories?: QueuedCategory[];
      unmet?: UnmetRequest[];
    };
    setCategories(data.categories ?? []);
    setUnmet(data.unmet ?? []);

    // Separate request, and deliberately non-fatal: the review queue is the
    // page's job, and a cost rollup that fails should not blank it.
    const costs = await fetch("/api/admin/costs", { credentials: "include" });
    if (costs.ok) setLedger((await costs.json()) as CostLedger);
  }, []);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const response = await fetch("/api/admin/costs/reconcile", {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        // The route states *why* it cannot reconcile (no `[ai]` binding, no
        // gateway id). Surfacing its sentence beats a generic failure toast.
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? "That didn't work");
      }
      const result = (await response.json()) as {
        examined: number;
        reconciled: number;
        unresolved: number;
        cacheStatusFilled: number;
        ambiguousLogIdRows: number;
      };
      const ambiguous =
        result.ambiguousLogIdRows > 0
          ? ` ${result.ambiguousLogIdRows} skipped: their gateway log is claimed by more than one row, so the cost cannot be assigned to any of them.`
          : "";
      toast({
        title: "Reconciled against AI Gateway",
        // Says what it could NOT do as well as what it could: a run that
        // resolves nothing must not read like a run with nothing to do.
        description: `${result.reconciled} of ${result.examined} priced, ${result.unresolved} still unknown, ${result.cacheStatusFilled} cache statuses filled.${ambiguous}`,
      });
      await load();
    } catch (e) {
      toast({
        title: "Couldn't reconcile",
        description: e instanceof Error ? e.message : "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setReconciling(false);
    }
  };

  useEffect(() => {
    load().catch(() => setDenied(true));
  }, [load]);

  const act = async (id: string, action: "approve" | "delete") => {
    setBusyId(id);
    try {
      const response = await fetch(`/api/admin/categories/${encodeURIComponent(id)}/${action}`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) throw new Error("That didn't work");
      toast({
        title: action === "approve" ? "Marked reviewed" : "Deleted",
        description:
          action === "approve"
            ? `${id} is now marked as reviewed.`
            : `${id} will be rebuilt from scratch next time someone searches for it.`,
      });
      await load();
    } catch (e) {
      toast({
        title: "Couldn't do that",
        description: e instanceof Error ? e.message : "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  if (denied) {
    return (
      <div className="mx-auto max-w-2xl py-12">
        <h1 className="text-3xl font-bold tracking-tight">Not found</h1>
        <p className="mt-2 text-muted-foreground">There's nothing here.</p>
      </div>
    );
  }

  const pending = (categories ?? []).filter((c) => c.reviewedAt === null);
  const reviewed = (categories ?? []).filter((c) => c.reviewedAt !== null);

  return (
    <div className="mx-auto max-w-3xl space-y-10 py-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Review queue</h1>
        <p className="text-muted-foreground">
          Categories built on the fly for a shopper. They ship unreviewed — a drafting model gives
          every category the same generic trust floors and can't know which ones are
          safety-sensitive. That judgment happens here.
        </p>
      </div>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold">
          Needs to review {pending.length > 0 && `(${pending.length})`}
        </h2>
        {categories !== null && pending.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing waiting.</p>
        )}
        {pending.map((category) => (
          <CategoryCard
            key={category.id}
            category={category}
            busy={busyId === category.id}
            onAct={act}
          />
        ))}
      </section>

      {unmet.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Asked for, not covered</h2>
          <p className="text-sm text-muted-foreground">
            What people searched for that we couldn't serve at all — the most direct answer to what
            deserves hand-authoring next. Requests, then how many distinct people we can name;
            requests from since-deleted accounts are unattributable and counted separately rather
            than guessed at.
          </p>
          <Card>
            <CardContent className="space-y-2 pt-6">
              {unmet.map((request) => (
                <div key={request.need} className="flex items-baseline justify-between gap-4">
                  <span className="text-sm">{request.need}</span>
                  <span className="label text-xs text-muted-foreground">
                    {askerSummary(request)}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      {ledger && <CostSection ledger={ledger} busy={reconciling} onReconcile={reconcile} />}

      {reviewed.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Reviewed</h2>
          {reviewed.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              busy={busyId === category.id}
              onAct={act}
            />
          ))}
        </section>
      )}
    </div>
  );
}

/**
 * Fee vs. cost vs. commission (#120).
 *
 * Written to be un-misreadable rather than tidy. Three things are kept
 * deliberately separate, and each of them was a way this view could have lied:
 *
 *  - **Measured vs. modelled.** AI cost comes from per-call figures; the Keepa
 *    figure is a fixed subscription apportioned across the tokens it buys. They
 *    are labelled and never added into a single "total cost".
 *  - **Local estimate vs. gateway figure.** Both are shown. They can drift, and
 *    the drift is the interesting part.
 *  - **Unknown vs. zero.** Every total carries the count of rows it could not
 *    price. A sum that silently treats unknown as free reads as healthy margin.
 */
function CostSection({
  ledger,
  busy,
  onReconcile,
}: {
  ledger: CostLedger;
  busy: boolean;
  onReconcile: () => void;
}) {
  const t = ledger.totals;
  const eurThenUsd = (eur: number, usdValue: number | null): string =>
    usdValue === null
      ? `€${eur.toFixed(6)} · USD unknown (no EUR_USD_RATE configured)`
      : `€${eur.toFixed(6)} · ${usd(usdValue)}`;
  const hitRate = (rate: number | null, known: number, total: number): string =>
    rate === null
      ? `unknown (0 of ${total} calls report a status)`
      : `${(rate * 100).toFixed(0)}% of ${known} calls with a known status`;

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-xl font-semibold">Fee vs. cost vs. commission</h2>
        <Button size="sm" variant="outline" disabled={busy} onClick={onReconcile}>
          Reconcile with AI Gateway
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        What we charged, what it cost, and what Amazon paid us — the three numbers the fee has never
        been checked against. Revenue is the fee <em>and</em> the commission; a margin read on the
        fee alone understates it.
      </p>

      <Card>
        <CardContent className="space-y-3 pt-6 text-sm">
          <Row label="Sessions" value={`${t.sessionCount}`} />
          <Row label="Fee quoted" value={cents(t.quotedFeeCents)} />
          <Row
            label="Commission (Associates)"
            value={
              t.unknownCommissionCount > 0
                ? `${cents(t.knownCommissionCents)} known · ${t.unknownCommissionCount} unknown`
                : cents(t.knownCommissionCents)
            }
          />
          <Row
            label="AI — local estimate (measured)"
            value={floorWithUnknowns(
              t.inference.localEstimateUsd,
              t.inference.unpricedEventCount,
              "over sessions",
            )}
          />
          <Row
            label="AI — AI Gateway figure (measured)"
            value={floorWithUnknowns(
              t.inference.gatewayCostUsd,
              t.inference.unreconciledEventCount,
              "over sessions",
            )}
          />
          <Row
            label="Keepa, sessions (MODELLED apportionment)"
            value={eurThenUsd(t.sessionKeepaCostEur, t.sessionKeepaCostUsd)}
          />
          <Row
            label="Cache hit rate (sessions)"
            value={hitRate(
              t.sessionCacheHitRate,
              t.inference.cacheKnownCount,
              t.inference.eventCount,
            )}
          />
          <Row
            label="Sessions with unpriced calls"
            value={`${t.sessionsWithUnknownCostCount} of ${t.sessionCount}`}
          />
        </CardContent>
      </Card>

      {/* A SECOND card, not more rows in the first one. Build spend is all-time
          and keyed by category; the session figures above cover the newest N
          sessions. Reporting them in one column invites a subtraction nobody
          meant — which is exactly what an earlier draft did by summing the two
          Keepa figures and blending the two cache denominators. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pre-session category builds — all time</CardTitle>
          <CardDescription>
            A different span from the figures above, which cover the newest{" "}
            {ledger.window.sessionLimit} sessions ({ledger.window.sessionCount} found). Build spend
            is not per session: it is a one-time cost per category, amortised over every later
            shopper in it. Never add the two.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Row
            label="AI — local estimate (measured)"
            value={floorWithUnknowns(
              t.buildInference.localEstimateUsd,
              t.buildInference.unpricedEventCount,
              "over all builds",
            )}
          />
          <Row
            label="AI — AI Gateway figure (measured)"
            value={floorWithUnknowns(
              t.buildInference.gatewayCostUsd,
              t.buildInference.unreconciledEventCount,
              "over all builds",
            )}
          />
          <Row
            label="Keepa, builds (MODELLED apportionment)"
            value={`${eurThenUsd(t.buildKeepaCostEur, t.buildKeepaCostUsd)} · ${t.buildKeepaTokens} tokens`}
          />
          <Row
            label="Cache hit rate (builds)"
            value={hitRate(
              t.buildCacheHitRate,
              t.buildInference.cacheKnownCount,
              t.buildInference.eventCount,
            )}
          />
        </CardContent>
      </Card>

      {/* Blended, and it has to say so. The gateway caches with a 3600s TTL and
          a hit is near-free, so these totals are the cost at whatever hit rate
          happened — not the cold-call cost a fee has to cover in the worst
          case. */}
      <p className="text-xs text-muted-foreground">
        Costs above are <strong>blended</strong> at the observed cache hit rate, not the cold-call
        cost. “Unknown” never means “$0”: it means no price exists for that call yet — an unpriced
        model, or a gateway log that has not been reconciled. Keepa is a fixed subscription
        apportioned across the tokens it buys (docs/DESIGN.md §5.1), so no invoice line moves when a
        session draws tokens.
      </p>

      {ledger.builds.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Build spend, by category</CardTitle>
            <CardDescription>
              Spent before any session existed — the first shopper in a category pays for the build,
              everyone after gets it from D1. A one-time cost per category, amortised across every
              later session in it, so it is reported here rather than added to one session's cost.
              Failed builds are included: they cost money too.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {ledger.builds.map((build) => (
              <div key={build.categoryId} className="flex items-baseline justify-between gap-4">
                <span className="text-sm">{build.categoryId}</span>
                <span className="label text-xs text-muted-foreground">
                  {build.buildCount}× build ·{" "}
                  {floorWithUnknowns(
                    build.inference.localEstimateUsd,
                    build.inference.unpricedEventCount,
                    "AI",
                  )}{" "}
                  · {build.keepaTokensConsumed} Keepa tokens
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {ledger.sessions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Per session</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {ledger.sessions.map((session) => (
              <div key={session.sessionId} className="flex items-baseline justify-between gap-4">
                <span className="text-sm">
                  {session.category}
                  <span className="text-muted-foreground"> · {session.openedAt}</span>
                </span>
                <span className="label text-xs text-muted-foreground">
                  fee {cents(session.quotedFeeCents)}
                  {session.feeWaived && " (waived)"} ·{" "}
                  {floorWithUnknowns(
                    session.inference.localEstimateUsd,
                    session.inference.unpricedEventCount,
                    "AI",
                  )}{" "}
                  · {session.keepaTokensConsumed} Keepa ·{" "}
                  {session.commissionCents === null
                    ? "commission unknown"
                    : `commission ${cents(session.commissionCents)}`}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="label text-xs">{value}</span>
    </div>
  );
}

function CategoryCard({
  category,
  busy,
  onAct,
}: {
  category: QueuedCategory;
  busy: boolean;
  onAct: (id: string, action: "approve" | "delete") => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg">{category.id}</CardTitle>
            <CardDescription>
              from “{category.sourceNeed}” · used {category.useCount}×
            </CardDescription>
          </div>
          {/* The distinction that decides whether we can claim completeness. */}
          <span
            className={`label text-xs ${
              category.groundingMode === "category" ? "text-muted-foreground" : "text-destructive"
            }`}
          >
            {category.groundingMode === "category" ? `node ${category.rootCategory}` : "title-only"}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <p className="label text-xs text-muted-foreground">Asks</p>
          {category.questions.map((question) => (
            <p key={question.question} className="text-sm">
              {question.question}
              {question.required && <span className="text-destructive"> *</span>}
            </p>
          ))}
        </div>

        {/* Where the safety judgment lives: generic floors are the thing most
            likely to be wrong on a generated category. */}
        {category.guardrails.length > 0 && (
          <div className="space-y-1">
            <p className="label text-xs text-muted-foreground">Applies silently</p>
            {category.guardrails.map((guardrail) => (
              <p key={guardrail} className="text-sm text-muted-foreground">
                {guardrail}
              </p>
            ))}
          </div>
        )}

        {/* Why the model accepted this node — the audit trail for a machine
            decision, without which "reviewed" would mean rubber-stamping. */}
        {category.evidence.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              How it picked this category
            </summary>
            <div className="mt-2 space-y-1">
              {category.evidence.map((line) => (
                <p key={line} className="text-xs text-muted-foreground">
                  {line}
                </p>
              ))}
            </div>
          </details>
        )}

        <div className="flex gap-2">
          {category.reviewedAt === null && (
            <Button size="sm" disabled={busy} onClick={() => onAct(category.id, "approve")}>
              Looks right
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onAct(category.id, "delete")}
          >
            Delete &amp; rebuild
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
